import React, { FC, useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const Login: FC = () => {
  const { t, i18n } = useTranslation();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname || '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = i18n.language.startsWith('fr')
      ? 'Connexion — AidFone'
      : 'Sign in — AidFone';
  }, [i18n.language]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const { error: signInError } = await signIn(email, password);
    setLoading(false);

    if (signInError) {
      setError(t('login.errorInvalidCredentials', 'Invalid email or password'));
      return;
    }
    navigate(redirectTo, { replace: true });
  };

  return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center px-4 font-body">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8">
          <img
            src="/images/landing/aidfone-logo-horizontal.png"
            alt="AidFone"
            className="h-10"
          />
        </Link>
        <div className="bg-white rounded-2xl border border-brand-border p-8 shadow-sm">
          <h1 className="text-2xl font-display font-bold text-brand-text mb-1">
            {t('login.title', 'Sign in')}
          </h1>
          <p className="text-sm text-brand-text-muted mb-6">
            {t('login.subtitle', 'Welcome back.')}
          </p>

          {error && (
            <div
              role="alert"
              className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-brand-text">
                {t('login.email', 'Email')}
              </span>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="px-4 py-3 rounded-xl border border-brand-border focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-brand-text">
                {t('login.password', 'Password')}
              </span>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="px-4 py-3 rounded-xl border border-brand-border focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none"
              />
            </label>
            <button
              type="submit"
              disabled={loading || !email || !password}
              className="py-3 bg-brand-primary text-white font-semibold rounded-xl hover:bg-brand-primary/90 transition-colors disabled:opacity-50"
            >
              {loading
                ? t('login.signingIn', 'Signing in...')
                : t('login.signIn', 'Sign in')}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-brand-border flex flex-col gap-3 text-sm">
            <Link
              to="/forgot-password"
              className="text-brand-primary hover:underline text-center"
            >
              {t('login.forgotPassword', 'Forgot password?')}
            </Link>
            <p className="text-center text-brand-text-muted">
              {t('login.noAccount', "Don't have an account?")}{' '}
              <Link
                to="/configure"
                className="text-brand-primary hover:underline font-medium"
              >
                {t('login.setUpPhone', 'Set up a phone')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
